import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { contactAPI } from '../services/api';
import { useScrollReveal } from '../hooks/useScrollReveal';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
} from "lucide-react";

const ContactPage = () => {
  const headerReveal = useScrollReveal({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    phone: '',
    subject: 'General Inquiry'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: "Phone",
      content: "+91 831 951 0854",
      description: "Call us during business hours",
    },
    {
      icon: <Mail size={24} />,
      title: "Email",
      content: "hello@domigo.com",
      description: "We'll respond within 24 hours",
    },
    {
      icon: <MapPin size={24} />,
      title: "Location",
      content: "Indore Bypass Rd, Indore 452016",
      description: "Visit Us During Our Working Hours!",
    },
    {
      icon: <Clock size={24} />,
      title: "Hours",
      content: "Mon-Sat: 9.30am - 7.30pm, Sun: 10am - 7.30pm",
      description: "Extended hours available",
    },
  ];

  return (
    <div className="min-h-screen visitor-page">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-16 lg:py-24">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${headerReveal.isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-12'
            }`}
        >
          <div className="inline-flex items-center justify-center mb-6">
            <div className="p-4 bg-gradient-pink rounded-3xl shadow-pink">
              <Phone className="text-white" size={48} />
            </div>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-6">
            Get in Touch
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have questions about our services? Want to book an appointment?
            We're here to help! Reach out to us through any of the following
            channels.
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-6 shadow-soft text-center hover:shadow-medium hover-lift transition-all duration-300"
              >
                <div className="p-3 bg-gradient-pink rounded-2xl text-white mb-4 flex justify-center items-center w-14 h-14 mx-auto shadow-pink">
                  {info.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {info.title}
                </h3>
                <p className="text-domigo-pink font-semibold mb-2">
                  {info.content}
                </p>
                <p className="text-gray-600 text-sm">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent mb-8">
                Send us a Message
              </h2>
              <form className="space-y-4 max-w-xl" onSubmit={async (e) => {
                e.preventDefault();

                // Validate all fields before allowing submit
                const newErrors = {};
                const validateAll = () => {
                  // Name
                  if (!formData.name || !formData.name.trim()) {
                    newErrors.name = 'Name is required';
                  } else if (formData.name.trim().length < 2) {
                    newErrors.name = 'Name must be at least 2 characters';
                  }

                  // Email
                  if (!formData.email || !formData.email.trim()) {
                    newErrors.email = 'Email is required';
                  } else {
                    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRe.test(formData.email.trim())) {
                      newErrors.email = 'Enter a valid email address';
                    }
                  }

                  // Phone
                  if (!formData.phone || !formData.phone.trim()) {
                    newErrors.phone = 'Phone number is required';
                  } else {
                    const digits = formData.phone.replace(/\D/g, '');
                    if (digits.length !== 10) {
                      newErrors.phone = 'Enter a valid 10-digit phone number';
                    }
                  }

                  // Message (at least 10 words)
                  if (!formData.message || !formData.message.trim()) {
                    newErrors.message = 'Message is required';
                  } else {
                    const words = formData.message.trim().split(/\s+/).filter(Boolean);
                    if (words.length < 5) {
                      newErrors.message = 'Message must be at least 5 words';
                    }
                  }
                };

                validateAll();

                if (Object.keys(newErrors).length > 0) {
                  setErrors(newErrors);
                  return;
                }

                // If valid, proceed
                setErrors({});

                try {
                  setSubmitting(true);
                  await contactAPI.submit(formData);
                  setSubmitSuccess(true);
                  setFormData({
                    name: '',
                    email: '',
                    message: '',
                    phone: '',
                    subject: 'General Inquiry'
                  });
                } catch (err) {
                  alert(err?.message || 'Failed to submit. Please try again later.');
                } finally {
                  setSubmitting(false);
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => ({ ...prev, name: v }));
                        setErrors((prev) => {
                          const next = { ...prev };
                          if (!v || !v.trim()) next.name = 'Name is required';
                          else if (v.trim().length < 2) next.name = 'Name must be at least 2 characters';
                          else delete next.name;
                          return next;
                        });
                      }}
                      className={`w-full px-4 py-2 border-2 rounded-xl focus:ring-2 transition-all ${errors.name ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-domigo-pink focus:border-domigo-pink'}`}
                      required
                    />
                    {errors.name && <p className="text-sm text-red-400 mt-2">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <select
                      value={formData.subject}
                      onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-domigo-pink focus:border-domigo-pink transition-all"
                    >
                      <option>General Inquiry</option>
                      <option>Booking Question</option>
                      <option>Service Information</option>
                      <option>Feedback</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({ ...prev, email: v }));
                      // real-time validate
                      setErrors((prev) => {
                        const next = { ...prev };
                        if (!v || !v.trim()) next.email = 'Email is required';
                        else {
                          const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRe.test(v.trim())) next.email = 'Enter a valid email address';
                          else delete next.email;
                        }
                        return next;
                      });
                    }}
                    className={`w-full px-4 py-2 border-2 rounded-xl focus:ring-2 transition-all ${errors.email ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-domigo-pink focus:border-domigo-pink'}`}
                    required
                  />
                  {errors.email && <p className="text-sm text-red-400 mt-2">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={formData.phone}
                    onChange={(e) => {
                      // allow only digits, limit to 10
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData((prev) => ({ ...prev, phone: digits }));
                      setErrors((prev) => {
                        const next = { ...prev };
                        if (!digits) next.phone = 'Phone number is required';
                        else if (digits.length !== 10) next.phone = 'Enter a valid 10-digit phone number';
                        else delete next.phone;
                        return next;
                      });
                    }}
                    className={`w-full px-4 py-2 border-2 rounded-xl focus:ring-2 transition-all ${errors.phone ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-domigo-pink focus:border-domigo-pink'}`}
                  />
                  {errors.phone && <p className="text-sm text-red-400 mt-2">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({ ...prev, message: v }));
                      setErrors((prev) => {
                        const next = { ...prev };
                        if (!v || !v.trim()) next.message = 'Message is required';
                        else {
                          const words = v.trim().split(/\s+/).filter(Boolean);
                          if (words.length < 5) next.message = 'Message must be at least 5 words';
                          else delete next.message;
                        }
                        return next;
                      });
                    }}
                    className={`w-full px-4 py-2 border-2 rounded-xl focus:ring-2 transition-all ${errors.message ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-domigo-pink focus:border-domigo-pink'}`}
                    placeholder="Tell us how we can help you and your pet..."
                    required
                  />
                  {errors.message && <p className="text-sm text-red-400 mt-2">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-pink text-white py-3 rounded-full text-base font-semibold hover:shadow-pink transition-all duration-300 transform hover:scale-105 flex items-center justify-center disabled:opacity-60"
                >
                  <Send className="mr-3" size={20} />
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
              {/* success modal is rendered globally below when submitSuccess is true */}
            </div>

            {/* Map */}
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent mb-8">
                Find Us
              </h2>
              <div className="rounded-3xl overflow-hidden shadow-large h-64 md:h-80 lg:h-96">
                <iframe
                  title="Domigo Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0, height: '100%' }}
                  loading="lazy"
                  allowFullScreen
                  src="https://www.google.com/maps?q=22.7392731,75.9336348&z=15&output=embed"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success confirmation modal (shows after successful API response) */}
      {submitSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity animate-fade-in" />

          <div className="relative bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-lg transform transition-all duration-300 animate-fade-in">
            <div className="flex flex-col items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="text-green-600" size={48} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Enquiry Submitted Successfully</h3>
              <p className="text-gray-600 text-center">
                Thank you for reaching out. Our team will contact you shortly.
              </p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="mt-4 bg-gradient-pink text-white py-2 px-6 rounded-full font-semibold hover:shadow-pink transition-all duration-300 transform hover:scale-105"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ContactPage;
